import { useMemo } from 'react'
import { parseISO } from 'date-fns'
import { useCurrentUsageQuery } from './useCurrentUsageQuery'
import { useMonthEndUsageQuery } from './useMonthEndUsageQuery'
import { daysElapsedInMonth, projectEndDate } from '../lib/periods'
import { MINUTES_ROUNDING_SCALE } from '../constants'
import { ADDON_LABELS, BILLING_ROW_LABELS } from '../types'
import type {
  AddonSetting,
  BillingAddonLine,
  CurrentUsageResponse,
  FtweEstimate,
  MinutesLedgerSummary,
  MonthEndUsageResponse,
  RateCardSnapshot,
  RateChange,
  UsdAndMinutes,
  UtilizationLine,
} from '../types'

export interface BillingPageData {
  isCurrent: boolean
  isLoading: boolean
  hasData: boolean
  /** True when a closed period exists but its monthly_twilio_adjustment
   *  ledger row hasn't been written yet (typically days 1-2 of the next
   *  month, while Twilio finalizes its counts). UI should show a friendly
   *  "period closing" state instead of an error. */
  pendingFinalization: boolean
  twilioPulledAt: Date | null
  utilizationLines: UtilizationLine[]
  totalMinutes: number
  totalUsd: number
  ledger: MinutesLedgerSummary
  forecast: { daysRemaining: number; endDate: Date } | null
  asOfDate: Date
  /** Concurrent human FTEs the agent's workload represents, as relay
   *  estimates them. Null when there is no data, or when the org has no rate
   *  card to estimate from. */
  concurrentFtes: number | null
  /** The current month's active rate card; null for a closed month or while
   *  no usage data has loaded. */
  rateCard: RateCardSnapshot | null
  /** Each rate a new card moved during the period, oldest first. */
  rateChanges: RateChange[]
  /** The add-ons on for the current month; none for a closed month. */
  addons: AddonSetting[]
}

const EMPTY_LEDGER: MinutesLedgerSummary = {
  startMinutes: 0,
  addedMinutes: 0,
  usedMinutes: 0,
  remainingMinutes: 0,
}

function num(value: string | null | undefined): number {
  return value == null ? 0 : Number(value)
}

/** The estimate as a number of concurrent FTEs, or null when relay had no
 *  rate card to estimate from. */
function concurrentFtesOf(ftwe: FtweEstimate): number | null {
  return ftwe.estimate === null ? null : Number(ftwe.estimate)
}

/** Build a single "Calls" parent line from its sub-rows (Conversation,
 *  Analysis, and Connection for closed months; the current month may carry a
 *  projected Connection sub-row in its place). Empty sub-rows (no minutes
 *  *and* no amount) are dropped; a row with a ledger amount but zero minutes
 *  (a min charge, rounding, or a Twilio adjustment) is kept so it stays in the
 *  receipt and the Total. The parent's quantity/amount are the sum of what
 *  remains; it is priced as its sub-rows are, and its count is the period's
 *  call count when the relay serves one. Projected rows go last. When nothing
 *  non-projected is billable, the projected rows stand on their own with no
 *  Calls group: close bills Connection whether or not the month has
 *  conversation, so a month whose only charge so far is Connection still
 *  shows its estimate. */
function callGroup(
  subRows: UtilizationLine[],
  count: number | undefined,
  pricing: Pricing
): UtilizationLine[] {
  const kept = subRows.filter((r) => r.quantityMinutes > 0 || r.amountUsd !== 0)
  const rows = kept.filter((r) => !r.isProjected)
  const projected = kept.filter((r) => r.isProjected)
  if (rows.length === 0) return projected
  const ordered = [...rows, ...projected]
  const minutes = ordered.reduce((s, r) => s + r.quantityMinutes, 0)
  const amountUsd = ordered.reduce((s, r) => s + r.amountUsd, 0)
  return [
    {
      description: 'Calls',
      quantityMinutes: minutes,
      pricePerMinute: pricePerMinute(amountUsd, minutes, pricing),
      amountUsd,
      count,
      subRows: ordered,
    },
  ]
}

/** The current month's estimated Connection (the Twilio minutes beyond the
 *  conversation minutes, after the non-conversation discount), by the relay's
 *  month-close formula. Connection is priced once, at the card in force at
 *  month end. A change that has already taken effect is in `rate_card`, and it
 *  reprices the whole month's Connection, as ChangeRateCardForm states. A
 *  card scheduled for later in the month is not in this response, so the
 *  estimate is at today's rates. For that reason it does not use `pricingOf`
 *  or `rate_changes`. Empty when there is no Twilio sync, no rate or discount,
 *  or nothing to bill. */
export function projectedConnectionLine(
  data: CurrentUsageResponse
): UtilizationLine[] {
  const { twilio_pulled_at, twilio_minutes, rate_card } = data
  if (
    twilio_pulled_at == null ||
    twilio_minutes == null ||
    data.convo_minutes == null ||
    rate_card.minute_rate == null ||
    rate_card.non_conversation_discount == null
  ) {
    return []
  }
  const twilio = Number(twilio_minutes)
  const convo = Number(data.convo_minutes)
  const rate = Number(rate_card.minute_rate)
  const discount = Number(rate_card.non_conversation_discount)
  // `Number` turns a malformed value into NaN, which slips past `<= 0`.
  if (![twilio, convo, rate, discount].every(Number.isFinite)) return []
  const raw = Math.max(0, twilio - convo) * (1 - discount)
  const quantityMinutes =
    Math.round(raw * MINUTES_ROUNDING_SCALE) / MINUTES_ROUNDING_SCALE
  if (quantityMinutes <= 0) return []
  return [
    {
      description: BILLING_ROW_LABELS.projectedConnection,
      quantityMinutes,
      pricePerMinute: rate,
      amountUsd: quantityMinutes * rate,
      isProjected: true,
    },
  ]
}

/** The minutes `lines` charge that the relay's balance has not taken yet:
 *  the Connection estimate, and what the add-on rows charge beyond the
 *  relay's own `addons` figures. Read off the lines as they are rendered, so
 *  Used moves with the Total however the add-on rows price the estimate. */
export function unbilledMinutes(
  lines: UtilizationLine[],
  addons: BillingAddonLine[] | undefined
): number {
  const estimate = lines
    .flatMap((line) => line.subRows ?? [line])
    .filter((line) => line.isProjected)
    .reduce((sum, line) => sum + line.quantityMinutes, 0)
  const addonRows = lines
    .filter((line) => line.usageFraction != null)
    .reduce((sum, line) => sum + line.quantityMinutes, 0)
  const billedAddons = (addons ?? []).reduce(
    (sum, addon) => sum + num(addon.minutes),
    0
  )
  return estimate + addonRows - billedAddons
}

/** How the period's lines are priced: at the card's minute `rate`, or, when
 *  the minute rate changed partway through, at each line's own blend. */
interface Pricing {
  rate: number
  blended: boolean
}

/** The period's pricing. Only a minute-rate change blends a line's price: a
 *  multiplier scales a line's minutes, not the rate they are billed at. */
function pricingOf(rate: number, rateChanges: RateChange[]): Pricing {
  return {
    rate,
    blended: rateChanges.some((change) => change.field === 'minute_rate'),
  }
}

/** A line's price per minute. With one minute rate all period it is the
 *  card's: each row's amount is rounded to 4 places, so a small line's
 *  amount over its minutes would read just off it ($0.5499 at $0.55). A
 *  period whose minute rate changed bills a line at two rates, so no single
 *  rate times its minutes comes to its amount; its amount over its minutes
 *  does. A line billed with no minutes is priced at the card's rate. */
function pricePerMinute(
  amountUsd: number,
  minutes: number,
  pricing: Pricing
): number {
  return pricing.blended && minutes > 0 ? amountUsd / minutes : pricing.rate
}

/** A usage sub-row under Calls. */
function usageRow(
  description: string,
  minutes: number,
  amountUsd: number,
  pricing: Pricing
): UtilizationLine {
  return {
    description,
    quantityMinutes: minutes,
    pricePerMinute: pricePerMinute(amountUsd, minutes, pricing),
    amountUsd,
  }
}

/** A metered-session row — computer use, Evaluation Rubrics, or David and
 *  Hanna time.
 *
 *  All are minutes scaled by a rate card multiplier and charged at the
 *  card's minute rate, so they render identically and share this. The row
 *  survives an amount arriving at zero minutes: a min charge, a rounding or
 *  an adjustment reaches the Total, and anything in the Total needs a row
 *  explaining it.
 */
function sessionLine(
  description: string,
  minutes: number,
  amountUsd: number,
  pricing: Pricing,
  count: number | undefined
): UtilizationLine[] {
  if (
    minutes <= 0 &&
    amountUsd === 0 &&
    !(description === BILLING_ROW_LABELS.documents && (count ?? 0) > 0)
  )
    return []
  return [
    {
      description,
      quantityMinutes: minutes,
      pricePerMinute: pricePerMinute(amountUsd, minutes, pricing),
      amountUsd,
      count,
    },
  ]
}

/** The metered-session fields the current and closed receipts both carry. */
type MeteredUsage = Pick<
  CurrentUsageResponse,
  | 'computer_use_minutes'
  | 'computer_use_amount'
  | 'session_count'
  | 'document_minutes'
  | 'document_amount'
  | 'document_count'
  | 'rubric_eval_minutes'
  | 'rubric_eval_amount'
  | 'rubric_eval_count'
  | 'david_session_minutes'
  | 'david_session_amount'
  | 'david_session_count'
>

/** The metered-session rows after the Calls group, in receipt order. */
function sessionLines(data: MeteredUsage, pricing: Pricing): UtilizationLine[] {
  return [
    ...sessionLine(
      BILLING_ROW_LABELS.computerSessions,
      num(data.computer_use_minutes),
      num(data.computer_use_amount),
      pricing,
      countOf(data.session_count)
    ),
    ...sessionLine(
      BILLING_ROW_LABELS.documents,
      num(data.document_minutes),
      num(data.document_amount),
      pricing,
      data.document_count
    ),
    ...sessionLine(
      BILLING_ROW_LABELS.evaluationRubrics,
      num(data.rubric_eval_minutes),
      num(data.rubric_eval_amount),
      pricing,
      data.rubric_eval_count
    ),
    ...sessionLine(
      BILLING_ROW_LABELS.davidSessions,
      num(data.david_session_minutes),
      num(data.david_session_amount),
      pricing,
      countOf(data.david_session_count)
    ),
  ]
}

/** Parse a count field: null/undefined (no count captured for the period)
 *  stays undefined so the table shows a blank Count cell instead of a
 *  misleading 0. */
function countOf(value: string | null | undefined): number | undefined {
  return value == null ? undefined : Number(value)
}

/** What the add-ons are a percentage of, stated as its own line so the
 *  receipt can be read top to bottom: the usage lines above it add up to
 *  this, and each add-on is that figure times its percentage. Only worth a
 *  row when an add-on follows it — otherwise the Total already says it. For
 *  the current month this is usage so far with the Connection estimate, which
 *  the add-ons below it are priced on too. */
function usageSubtotalLine(
  usageLines: UtilizationLine[],
  addons: UtilizationLine[]
): UtilizationLine[] {
  if (addons.length === 0) return []
  return [
    {
      description: 'Usage subtotal',
      quantityMinutes: 0,
      pricePerMinute: 0,
      amountUsd: usageLines.reduce((sum, line) => sum + line.amountUsd, 0),
      isSubtotal: true,
    },
  ]
}

/** A row per add-on on for the period, carrying the minutes it takes from the
 *  balance, so the table's minutes add up to the Used the Minutes Ledger
 *  shows; its dollars are in the Total. A current-month add-on is not final:
 *  the relay prices it on usage so far, and its percentage of the `estimate`
 *  lines is added here, as close will charge it on Connection. */
function addonLines(
  addons: BillingAddonLine[] | undefined,
  estimate: UtilizationLine[]
): UtilizationLine[] {
  const estimateUsd = estimate.reduce((sum, line) => sum + line.amountUsd, 0)
  const estimateMinutes = estimate.reduce(
    (sum, line) => sum + line.quantityMinutes,
    0
  )
  return (addons ?? []).map((addon) => {
    const fraction = num(addon.fraction)
    return {
      description: ADDON_LABELS[addon.addon],
      quantityMinutes: num(addon.minutes) + fraction * estimateMinutes,
      pricePerMinute: 0,
      amountUsd: num(addon.amount) + fraction * estimateUsd,
      usageFraction: fraction,
    }
  })
}

// Credits we *gave* (credit_compensation) reduce the bill. `amountUsd` is the
// ledger value (<= 0) so it nets out of the Total; the minutes are shown but
// excluded from the minutes total (a credit isn't service used). Rendered
// whenever a comp credit landed in the period — keyed on amount too, so a
// credit that nets the Total isn't dropped just because it carries no minutes.
function creditsLine(minutes: number, amountUsd: number): UtilizationLine[] {
  if (minutes <= 0 && amountUsd === 0) return []
  return [
    {
      description: 'Compensation credits',
      quantityMinutes: minutes,
      // The credit's own effective rate (magnitude of amount ÷ its minutes) —
      // not the call/rate-card rate, which can differ from what the credit was
      // booked at. 0 when it's an amount-only (0-minute) credit.
      pricePerMinute: minutes > 0 ? Math.abs(amountUsd) / minutes : 0,
      amountUsd,
      isCredit: true,
    },
  ]
}

/** The receipt's totals. The subtotal restates the usage lines above it, so
 *  counting it again would bill that usage twice; credits carry minutes that
 *  are not service used, so they stay out of the minutes. */
export function receiptTotals(lines: UtilizationLine[]): UsdAndMinutes {
  const counted = lines.filter((line) => !line.isSubtotal)
  return {
    usd: counted.reduce((sum, line) => sum + line.amountUsd, 0),
    minutes: counted
      .filter((line) => !line.isCredit)
      .reduce((sum, line) => sum + line.quantityMinutes, 0),
  }
}

/** A closed month's minutes total. The relay's `total_minutes` is the usage
 *  alone; the add-on minutes the close charged come on top, as they do in the
 *  period's Used, or the receipt reads short of what the balance was charged. */
export function closedMonthTotalMinutes(data: MonthEndUsageResponse): number {
  return (data.addons ?? []).reduce(
    (sum, addon) => sum + num(addon.minutes),
    num(data.total_minutes)
  )
}

// Per-line amounts are ledger-truth straight from the RPC (SUM of amount per
// entry type); the receipt no longer re-derives them as minutes × rate. Calls
// is one line with Conversation / Analysis sub-rows, plus a projected
// Connection (estimate) sub-row; in a month with no Conversation or Analysis
// yet, the estimate is a top-level line of its own. Computer Use is its own
// line.
export function deriveCurrentLines(
  data: CurrentUsageResponse
): UtilizationLine[] {
  const pricing = pricingOf(num(data.rate_card.minute_rate), data.rate_changes)
  // The current month counts an estimate of Connection (the Twilio adjustment)
  // in the Calls sums, the Usage subtotal, the add-ons and the Total, as close
  // will bill it. The ledger adds it to Used to match.
  const estimate = projectedConnectionLine(data)
  const addons = addonLines(data.addons, estimate)
  const call = callGroup(
    [
      usageRow(
        BILLING_ROW_LABELS.conversation,
        num(data.convo_minutes),
        num(data.conversation_amount),
        pricing
      ),
      usageRow(
        BILLING_ROW_LABELS.analysis,
        num(data.analysis_minutes),
        num(data.analysis_amount),
        pricing
      ),
      ...estimate,
    ],
    countOf(data.call_count),
    pricing
  )
  const usage = [...call, ...sessionLines(data, pricing)]
  return [
    ...usage,
    ...usageSubtotalLine(usage, addons),
    ...addons,
    ...creditsLine(
      num(data.period_compensation_credits_minutes),
      num(data.period_compensation_credits)
    ),
  ]
}

export function deriveMonthEndLines(
  data: MonthEndUsageResponse
): UtilizationLine[] {
  const pricing = pricingOf(num(data.minute_rate), data.rate_changes)
  const addons = addonLines(data.addons, [])
  const call = callGroup(
    [
      usageRow(
        BILLING_ROW_LABELS.conversation,
        num(data.conversation_minutes),
        num(data.conversation_amount),
        pricing
      ),
      usageRow(
        BILLING_ROW_LABELS.analysis,
        num(data.analysis_minutes),
        num(data.analysis_amount),
        pricing
      ),
      usageRow(
        BILLING_ROW_LABELS.connection,
        num(data.twilio_adjustment_minutes),
        num(data.twilio_adjustment_amount),
        pricing
      ),
    ],
    countOf(data.call_count),
    pricing
  )
  const usage = [...call, ...sessionLines(data, pricing)]
  return [
    ...usage,
    ...usageSubtotalLine(usage, addons),
    ...addons,
    ...creditsLine(
      num(data.period_compensation_credits_minutes),
      num(data.period_compensation_credits)
    ),
  ]
}

export function useBillingPageData(
  period: string,
  isCurrent: boolean
): BillingPageData {
  const currentQuery = useCurrentUsageQuery(isCurrent)
  const monthEndQuery = useMonthEndUsageQuery(period, !isCurrent)

  return useMemo(() => {
    // `now` is captured per-fetch (memo deps are the query payloads): all
    // wall-clock-derived values (asOfDate, daysElapsed, forecast.endDate)
    // describe the same point in time as the data they were computed from. On
    // a long-open tab `asOfDate` can drift behind real time until the next
    // refetch — that's intentional, navigating away/back forces a fresh fetch
    // and a fresh `now`.
    const now = new Date()

    if (isCurrent) {
      const data = currentQuery.data
      const isLoading = currentQuery.isLoading

      if (!data) {
        return {
          isCurrent: true,
          isLoading,
          hasData: false,
          pendingFinalization: false,
          twilioPulledAt: null,
          utilizationLines: [],
          totalMinutes: 0,
          totalUsd: 0,
          ledger: EMPTY_LEDGER,
          forecast: null,
          asOfDate: now,
          concurrentFtes: null,
          rateCard: null,
          addons: [],
          rateChanges: [],
        }
      }

      const lines = deriveCurrentLines(data)
      const { usd: totalUsd, minutes: totalMinutes } = receiptTotals(lines)
      // The balance pays for Connection at close, so Used and Remaining count
      // the estimate, with what the add-ons take on it, as spent, and Used
      // still matches the Total.
      const owed = unbilledMinutes(lines, data.addons)
      const remaining = num(data.period_end_balance_minutes) - owed
      const used = num(data.period_used_minutes) + owed
      const ledger: MinutesLedgerSummary = {
        startMinutes: num(data.period_start_balance_minutes),
        addedMinutes: num(data.period_credits_added_minutes),
        usedMinutes: used,
        remainingMinutes: remaining,
      }
      const daysElapsed = Math.max(daysElapsedInMonth(now), 1)
      const dailyBurn = used / daysElapsed
      const daysRemaining = dailyBurn > 0 ? remaining / dailyBurn : 0
      const forecast =
        dailyBurn > 0 && remaining > 0
          ? { daysRemaining, endDate: projectEndDate(daysRemaining, now) }
          : null

      return {
        isCurrent: true,
        isLoading,
        hasData: true,
        pendingFinalization: false,
        twilioPulledAt: data.twilio_pulled_at
          ? new Date(data.twilio_pulled_at)
          : null,
        utilizationLines: lines,
        totalMinutes,
        totalUsd,
        ledger,
        forecast,
        asOfDate: now,
        concurrentFtes: concurrentFtesOf(data.ftwe),
        rateCard: data.rate_card,
        // The add-on lines relay priced for this month. Relay leaves one off
        // only when the card's minute rate is 0, and an add-on cannot be
        // switched on at such a rate, so every add-on that is on has a line.
        addons: data.addons ?? [],
        rateChanges: data.rate_changes,
      }
    }

    const data = monthEndQuery.data
    const isLoading = monthEndQuery.isLoading

    if (!data) {
      // `null` (vs `undefined`) signals the 404 path from
      // `useMonthEndUsageQuery`: the period is closed but the close job
      // hasn't written `monthly_twilio_adjustment` yet. We surface this so
      // the page can render a "period closing" message instead of a
      // generic empty/error state. `undefined` (still loading, or a
      // genuine miss) keeps the old behavior.
      const pendingFinalization = data === null
      return {
        isCurrent: false,
        isLoading,
        hasData: false,
        pendingFinalization,
        twilioPulledAt: null,
        utilizationLines: [],
        totalMinutes: 0,
        totalUsd: 0,
        ledger: EMPTY_LEDGER,
        forecast: null,
        asOfDate: now,
        concurrentFtes: null,
        rateCard: null,
        addons: [],
        rateChanges: [],
      }
    }

    const lines = deriveMonthEndLines(data)
    const totalMinutes = closedMonthTotalMinutes(data)
    // total_amount is gross (debit-type entries only); net out comp credits,
    // whose amount is <= 0, so the receipt Total = Call + CU − Credits.
    const totalUsd =
      num(data.total_amount) + num(data.period_compensation_credits)
    const ledger: MinutesLedgerSummary = {
      startMinutes: num(data.period_start_balance_minutes),
      addedMinutes: num(data.period_credits_added_minutes),
      usedMinutes: num(data.period_used_minutes) || totalMinutes,
      remainingMinutes: num(data.period_end_balance_minutes),
    }
    // period_end is YYYY-MM-DD; parseISO returns local midnight, not UTC
    // midnight, so the rendered "as of" date stays on the right calendar day in
    // negative-offset timezones.
    const periodEnd = parseISO(data.period_end)
    return {
      isCurrent: false,
      isLoading,
      hasData: true,
      pendingFinalization: false,
      twilioPulledAt: data.twilio_finalized_at
        ? new Date(data.twilio_finalized_at)
        : null,
      utilizationLines: lines,
      totalMinutes,
      totalUsd,
      ledger,
      forecast: null,
      asOfDate: periodEnd,
      concurrentFtes: concurrentFtesOf(data.ftwe),
      rateCard: null,
      addons: [],
      rateChanges: data.rate_changes,
    }
  }, [
    isCurrent,
    currentQuery.data,
    currentQuery.isLoading,
    monthEndQuery.data,
    monthEndQuery.isLoading,
  ])
}
